import "./PanierMagasin.css";
import { useState, useEffect, useCallback } from "react";
import PanierMagasinMobile from "./PanierMagasinMobile";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import AfficheMagasinsService from "../../../Services/Magasin/AfficheMagasinsService";
import { MagasinInfo } from "../../../Modeles/MagasinModel";
import axios from "axios";
import PanierLengVisiteur from "../../../Services/Panier/PanierLeng";
import QrPaymentModal from "../../../components/QrPaymentModal";
import OverlayLoader from "../../../components/OverlayLoader";
import DefaulteImage from "../../Utilisateure/visiteur/DefaulteImage.jpeg";

/* ══════════════════════════════════════
   TYPES
══════════════════════════════════════ */
interface PanierItem {
  id: string;
  Designation: string;
  Image: string;
  price: string;
  quantity: number;
  Remise?: any;
  RefArticle?: string;
  NomMagasin?: string;
}

interface SocieteLivraison {
  id: number;
  NomSociete: string;
  AdresseSociete: string;
  Prix: string;
  Status: string;
  Date: string;
}

interface OrderForm {
  nom: string;
  adresse: string;
  modeLivraison: "magasin" | "societe";
  selectedMagasinId: any | null;
  selectedSocieteId: any | null;
}

/** en-compte info for a specific seller */
interface EnCompteInfo {
  en_compte: boolean;
  solde: number;
  entry_id: number | null;
  seller_id: number | null;
  seller_name: string;
}

/** One sub-order per seller */
interface SubOrder {
  sellerName: string;
  items: PanierItem[];
  total: number;
  enCompte: EnCompteInfo | null;          // loaded async
  paymentMethod: "solde" | "rib";         // chosen by user
}

/* ══════════════════════════════════════
   HELPERS
══════════════════════════════════════ */
function itemTotal(item: PanierItem): number {
  const unit = Number(String(item.price).replace(",", ".")) || 0;
  return unit * Number(item.quantity);
}

function groupByMagasin(items: PanierItem[]): Record<string, PanierItem[]> {
  return items.reduce<Record<string, PanierItem[]>>((acc, item) => {
    const key = (item.NomMagasin || "").trim() || "Inconnu";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

/* ══════════════════════════════════════
   COMPONENT
══════════════════════════════════════ */
export default function PanierMagasin() {
  const MagasinId      = localStorage.getItem("MagasinId")   ?? "";
  const MagasinName    = localStorage.getItem("MagasinName") ?? "";
  const AdresseMagasin = localStorage.getItem("adresse")     ?? "";

  /* ── API cart ── */
  const [PanierLen, setPanierLen]   = useState<PanierItem[]>([]);
  const [Totale, setTotale]         = useState<number>(0);
  const [loading, setLoading]       = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [clearingCart, setClearingCart] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [orderProgressMessage, setOrderProgressMessage] = useState<string>("Enregistrement de la commande...");

  /* ── QR Payment ── */
  const [showPayment, setShowPayment]               = useState(false);
  const [commandeIdForPayment, setCommandeIdForPayment] = useState("");

  /* ── Societies ── */
  const [societes, setSocietes]         = useState<SocieteLivraison[]>([]);
  const [loadingSocietes, setLoadingSocietes] = useState(false);
  const [selectedSociete, setSelectedSociete] = useState<SocieteLivraison | null>(null);

  /* ── Sub-orders (split by seller) ── */
  const [subOrders, setSubOrders] = useState<SubOrder[]>([]);
  const [loadingEnCompte, setLoadingEnCompte] = useState(false);

  /* ── Notifications ── */
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const notify = (text: string, type: "success" | "error" = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), type === "error" ? 7000 : 4000);
  };

  /* ── Fetch cart ── */
  const fetchPanier = useCallback(() => {
    setLoading(true);
    PanierLengVisiteur(MagasinId, "magasins")
      .GetLenPanier()
      .then((r: any) => setPanierLen(r.data))
      .catch((e: any) => console.error(e))
      .finally(() => setLoading(false));
  }, [MagasinId]);

  useEffect(() => { fetchPanier(); }, [fetchPanier]);

  /* ── Recompute total ── */
  useEffect(() => {
    const t = PanierLen.reduce((acc, i) => acc + itemTotal(i), 0);
    setTotale(parseFloat(t.toFixed(2)));
  }, [PanierLen]);

  /* ── Societies ── */
  useEffect(() => {
    setLoadingSocietes(true);
    axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison`)
      .then(({ data }) => setSocietes((data as SocieteLivraison[]).filter(s => s.Status === "active")))
      .catch(console.error)
      .finally(() => setLoadingSocietes(false));
  }, []);

  /* ── Build sub-orders when cart changes ── */
  useEffect(() => {
    const grouped = groupByMagasin(PanierLen);
    const orders: SubOrder[] = Object.entries(grouped).map(([seller, items]) => ({
      sellerName: seller,
      items,
      total: parseFloat(items.reduce((s, i) => s + itemTotal(i), 0).toFixed(2)),
      enCompte: null,
      paymentMethod: "rib",
    }));
    setSubOrders(orders);
  }, [PanierLen]);

  /* ── Magasins for pickup ── */
  const [loadingMagasins, setLoadingMagasins] = useState(false);
  const [AllMagasins, setAllMagasins]         = useState<MagasinInfo[]>([]);

  useEffect(() => {
    setLoadingMagasins(true);
    AfficheMagasinsService()
      .getMagasin()
      .then(res => {
        const filtered = (res.data as MagasinInfo[]).filter(m => m.NomMagasin !== MagasinName);
        setAllMagasins(filtered);
      })
      .catch(console.error)
      .finally(() => setLoadingMagasins(false));
  }, [MagasinName]);

  /* ── Order form ── */
  const [showModal,  setShowModal]  = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof OrderForm, string>>>({});
  const [orderForm,  setOrderForm]  = useState<OrderForm>({
    nom:               MagasinName,
    adresse:           AdresseMagasin,
    modeLivraison:     "magasin",
    selectedMagasinId: null,
    selectedSocieteId: null,
  });

  const currentFrais = orderForm.modeLivraison === "magasin"
    ? 0
    : selectedSociete ? (selectedSociete.Prix === "-" ? 0 : parseFloat(selectedSociete.Prix || "0")) : 0;

  const totalAvecFrais = Totale + currentFrais;

  /* ── Load en-compte info for each sub-order when modal opens ── */
  const loadEnCompteForSubOrders = useCallback(async (orders: SubOrder[]) => {
    if (!MagasinId || orders.length === 0) return;
    setLoadingEnCompte(true);
    const updated = await Promise.all(
      orders.map(async (o) => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/en-compte-by-supplier`,
            { params: { seller_name: o.sellerName } }
          );
          const info: EnCompteInfo = res.data;
          return {
            ...o,
            enCompte: info,
            paymentMethod: (info.en_compte && info.solde >= o.total ? "solde" : "rib") as "solde" | "rib",
          };
        } catch {
          return { ...o, enCompte: { en_compte: false, solde: 0, entry_id: null, seller_id: null, seller_name: o.sellerName }, paymentMethod: "rib" as "solde" | "rib" };
        }
      })
    );
    setSubOrders(updated);
    setLoadingEnCompte(false);
  }, [MagasinId]);

  /* Open modal → load en-compte */
  const handleOpenModal = async () => {
    setShowModal(true);
    await loadEnCompteForSubOrders(subOrders);
  };

  /* ── Validation ── */
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof OrderForm, string>> = {};
    if (!orderForm.nom.trim())     errors.nom     = "Champ requis";
    if (!orderForm.adresse.trim()) errors.adresse = "Champ requis";
    if (orderForm.modeLivraison === "magasin" && !orderForm.selectedMagasinId)
      errors.selectedMagasinId = "Veuillez choisir un magasin de retrait";
    if (orderForm.modeLivraison === "societe" && !selectedSociete)
      errors.selectedSocieteId = "Veuillez choisir une société de livraison";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── Confirm order with Step-by-Step feedback ── */
  const handleConfirm = async () => {
    if (!validateForm()) return;
    setConfirmingOrder(true);

    const selectedMag = AllMagasins.find(m => m.IdMagasin === orderForm.selectedMagasinId);

    try {
      let lastIdBl = "";
      const totalOrdersCount = subOrders.length;

      // 🔁 Submit one commande per sub-order (split by seller)
      for (let i = 0; i < subOrders.length; i++) {
        const sub = subOrders[i];
        const orderIndexLabel = totalOrdersCount > 1 ? `[${i + 1}/${totalOrdersCount}] ` : "";
        const subTotal = sub.total;
        const subFrais = totalOrdersCount === 1 ? currentFrais : 0;
        const subTotalTTC = parseFloat((subTotal + subFrais).toFixed(2));

        let methodPayment = "A la livraison";
        let statutPayment = "en cours..";
        if (sub.paymentMethod === "solde" && sub.enCompte?.en_compte) {
          methodPayment = "Solde en compte";
          statutPayment = "Payé par solde";
        }

        const formData = new FormData();
        formData.append("adress",           orderForm.adresse);
        formData.append("NomClient",        orderForm.nom);
        formData.append("SousClient",       "");
        formData.append("Remarque",         "");
        formData.append("TotalHT",          subTotal.toString());
        formData.append("TotalTTC",         subTotalTTC.toString());
        formData.append("NomMagasin",       MagasinName || "");
        formData.append("IdMagasin",        MagasinId   || "");
        formData.append("TypeUtilisateur",  "magasin");
        formData.append("ModeLivraison",    orderForm.modeLivraison);
        formData.append("FraisLivraison",   subFrais.toString());
        formData.append("MagasinRetrait",   orderForm.modeLivraison === "magasin" && selectedMag
          ? selectedMag.NomMagasin
          : selectedSociete ? selectedSociete.NomSociete : "Société de livraison");
        formData.append("AdresseRetreit",   orderForm.modeLivraison === "magasin" && selectedMag
          ? selectedMag.adresse_de_siege
          : selectedSociete ? selectedSociete.AdresseSociete : "");
        formData.append("SocieteLivraison", orderForm.modeLivraison === "societe" && selectedSociete
          ? selectedSociete.NomSociete : "");
        formData.append("MethodPayment",    methodPayment);
        formData.append("StatutPayment",    statutPayment);
        // Pass entry_id so the backend can verify solde before creating the order
        if (sub.paymentMethod === "solde" && sub.enCompte?.entry_id) {
          formData.append("entry_id", String(sub.enCompte.entry_id));
        }

        // Articles for this sub-order
        sub.items.forEach((item: any, index: number) => {
          for (const key in item) {
            if (Object.prototype.hasOwnProperty.call(item, key)) {
              formData.append(`detail[${index}][${key}]`, String(item[key]).replace(",", "."));
            }
          }
        });

        // 1️⃣ Enregistrer la commande
        setOrderProgressMessage(`${orderIndexLabel}Enregistrement de la commande pour "${sub.sellerName}"...`);
        const resp = await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/enregistrerCommande`,
          formData
        );
        lastIdBl = String(resp.data.IdBl ?? "");
        notify(`${orderIndexLabel}Commande "${sub.sellerName}" enregistrée avec succès (N° BL : ${lastIdBl})`, "success");

        // 2️⃣ Envoyer les articles liés à la commande
        setOrderProgressMessage(`${orderIndexLabel}Envoi des ${sub.items.length} articles pour "${sub.sellerName}" (BL : ${lastIdBl})...`);
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/sendArticleCommande/${resp.data.IdBl}`,
          { DataTable: sub.items },
          { headers: { "Content-Type": "application/json" } }
        );
        notify(`${orderIndexLabel}Articles envoyés pour "${sub.sellerName}"`, "success");

        // 3️⃣ Déduire le solde si paiement en compte
        if (sub.paymentMethod === "solde" && sub.enCompte?.en_compte && sub.enCompte.entry_id) {
          try {
            setOrderProgressMessage(`${orderIndexLabel}Déduction du solde en compte (${subTotal} MAD)...`);
            const soldeResp = await axios.post(
              `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/pay-by-solde`,
              { entry_id: sub.enCompte.entry_id, amount: subTotal }
            );
            notify(`${orderIndexLabel}Paiement par solde (${subTotal} MAD) validé pour "${sub.sellerName}"`, "success");
            // ✅ Real-time: update localStorage solde so the banner shows correct balance
            if (soldeResp.data?.new_solde !== undefined) {
              localStorage.setItem('en_compte_solde', String(soldeResp.data.new_solde));
            }
          } catch (soldeErr: any) {
            const msg = soldeErr?.response?.data?.message || "Erreur déduction solde";
            notify(`⚠️ ${sub.sellerName}: ${msg}`, "error");
          }
        }
      }

      // 4️⃣ Nettoyer le panier
      setOrderProgressMessage("Finalisation et vidage du panier...");
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/delete/all/${MagasinId}`);
      localStorage.removeItem("cartExpiration");
      window.dispatchEvent(new Event("panier-updated"));

      notify(`🎉 Toutes les commandes ont été confirmées avec succès ! Merci ${orderForm.nom}.`, "success");
      setShowModal(false);
      setPanierLen([]);
      setOrderForm({ nom: MagasinName, adresse: AdresseMagasin, modeLivraison: "magasin", selectedMagasinId: null, selectedSocieteId: null });
      setSelectedSociete(null);

      // Only show QR/RIB popup when at least one sub-order is paid by RIB (not all by solde)
      const hasRibPayment = subOrders.some(s => s.paymentMethod === "rib");
      if (lastIdBl && hasRibPayment) {
        setCommandeIdForPayment(lastIdBl);
        setShowPayment(true);
      }

    } catch (error: any) {
      console.error("Erreur commande:", error);
      const serverMsg = error?.response?.data?.message;
      if (serverMsg) {
        notify(serverMsg, "error");
      } else {
        notify("Une erreur est survenue lors de la commande", "error");
      }
    } finally {
      setConfirmingOrder(false);
    }
  };

  /* ── Delete item ── */
  const handleDeleteProduct = async (id: any, e: React.FormEvent) => {
    e.preventDefault();
    setDeletingId(id);
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/delete`, { id });
      const newPanier = PanierLen.filter((item: any) => item.id !== id);
      setPanierLen(newPanier);
      if (newPanier.length === 0) {
        localStorage.removeItem("cartExpiration");
        window.dispatchEvent(new Event("panier-updated"));
      }
    } catch (err) { console.error(err); }
    finally { setDeletingId(null); }
  };

  /* ── Clear cart ── */
  const handleVider = async () => {
    setClearingCart(true);
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/delete/all/${MagasinId}`);
      setPanierLen([]);
      localStorage.removeItem("cartExpiration");
      window.dispatchEvent(new Event("panier-updated"));
      notify("Panier vidé avec succès");
    } catch { notify("Impossible de vider le panier", "error"); }
    finally { setClearingCart(false); }
  };

  /* ── Update quantity ── */
  const handleQtyChange = async (id: string, delta: number) => {
    const item = PanierLen.find(p => p.id === id);
    if (!item) return;
    const newQty = Math.max(1, item.quantity + delta);
    setPanierLen(prev => prev.map(p => p.id === id ? { ...p, quantity: newQty } : p));
    try {
      const fd = new FormData();
      fd.append("quantity", newQty.toString());
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/update/${id}`, fd);
    } catch {
      setPanierLen(prev => prev.map(p => p.id === id ? { ...p, quantity: item.quantity } : p));
      notify("Impossible de mettre à jour la quantité", "error");
    }
  };

  /* ── Responsive ── */
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const h = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  if (screenWidth <= 450) return <PanierMagasinMobile />;

  const selectedMagasin = AllMagasins.find(m => m.IdMagasin === orderForm.selectedMagasinId);

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <>
      <NewSideBarMagasins />

      {/* Toast */}
      {message && (
        <div className={`pm-toast pm-toast--${message.type}`}>
          <span className="pm-toast__icon">{message.type === "success" ? "✓" : "✕"}</span>
          {message.text}
        </div>
      )}

      <div className="pm-layout">

        {/* ── Header ── */}
        <div className="pm-header">
          <div className="pm-header__left">
            <h1 className="pm-header__title">Mon Panier</h1>
            <span className="pm-badge">
              {PanierLen.length} article{PanierLen.length !== 1 ? "s" : ""}
            </span>
            {subOrders.length > 1 && (
              <span style={{ marginLeft: 10, fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
                → Sera divisé en {subOrders.length} commandes
              </span>
            )}
          </div>
          {PanierLen.length > 0 && (
            <button className="pm-btn-ghost" onClick={handleVider} disabled={clearingCart}>
              {clearingCart ? (
                <><span className="pm-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Vidage...</>
              ) : (
                <><svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                </svg> Vider le panier</>
              )}
            </button>
          )}
        </div>

        <div className="pm-body">

          {/* ── Product list grouped by seller ── */}
          <div className="pm-list">
            {loading ? (
              <div className="pm-magasin-loading">
                <span className="pm-spinner" /> Chargement du panier…
              </div>
            ) : PanierLen.length === 0 ? (
              <div className="pm-empty">
                <div className="pm-empty__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                </div>
                <h3 className="pm-empty__title">Votre panier est vide</h3>
                <p className="pm-empty__sub">Parcourez notre catalogue et ajoutez des produits</p>
              </div>
            ) : (
              /* ── Grouped by seller ── */
              subOrders.map((sub) => (
                <div key={sub.sellerName} style={{ marginBottom: 28 }}>
                  {/* Seller header */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    marginBottom: 10, paddingBottom: 6,
                    borderBottom: "2px solid #FFC400",
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#FFC400" strokeWidth="2" width="16" height="16">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>
                      {sub.sellerName}
                    </span>
                    <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 4 }}>
                      ({sub.items.length} article{sub.items.length > 1 ? "s" : ""})
                    </span>
                    <span style={{ marginLeft: "auto", fontWeight: 700, fontSize: 13, color: "#1a1a1a" }}>
                      {sub.total.toLocaleString("fr-MA")} MAD
                    </span>
                  </div>

                  {/* Items for this seller */}
                  {sub.items.map((item, index) => (
                    <div key={item.id} className="pm-card" style={{ animationDelay: `${index * 60}ms` }}>
                      <div className="pm-card__img-wrap">
                        <img
                          src={item.Image || DefaulteImage}
                          alt={item.Designation}
                          className="pm-card__img"
                          onError={(e) => { (e.target as HTMLImageElement).src = DefaulteImage; }}
                        />
                      </div>
                      <div className="pm-card__body">
                        <div className="pm-card__top">
                          <h4 className="pm-card__name">{item.Designation}</h4>
                          <button
                            className="pm-card__del"
                            disabled={deletingId === item.id}
                            onClick={(e) => handleDeleteProduct(item.id, e)}
                          >
                            {deletingId === item.id ? (
                              <span className="pm-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                            ) : (
                              <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <div className="pm-card__bottom">
                          <div className="pm-qty">
                            <button className="pm-qty__btn" onClick={() => handleQtyChange(item.id, -1)}>−</button>
                            <span className="pm-qty__val">{item.quantity}</span>
                            <button className="pm-qty__btn" onClick={() => handleQtyChange(item.id, +1)}>+</button>
                          </div>
                          <div className="pm-card__price">
                            {itemTotal(item).toLocaleString("fr-MA")} MAD
                          </div>
                        </div>
                        {item.quantity > 1 && (
                          <div className="pm-card__unit-price">
                            {(Number(String(item.price).replace(",", ".")) || 0).toLocaleString("fr-MA")} MAD / unité
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          {/* ── Summary ── */}
          {PanierLen.length > 0 && (
            <div className="pm-summary">
              <div className="pm-summary__title">Récapitulatif</div>
              <div className="pm-summary__rows">
                {subOrders.map(sub => (
                  <div className="pm-summary__row" key={sub.sellerName}>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                      {sub.sellerName}
                    </span>
                    <span>{sub.total.toLocaleString("fr-MA")} MAD</span>
                  </div>
                ))}
                {orderForm.modeLivraison !== "magasin" && (
                  <div className="pm-summary__row">
                    <span>{selectedSociete ? `Frais — ${selectedSociete.NomSociete}` : "Frais de livraison"}</span>
                    <span>{currentFrais.toLocaleString("fr-MA")} MAD</span>
                  </div>
                )}
                <div className="pm-summary__divider" />
                <div className="pm-summary__total">
                  <span>Total</span>
                  <span>{totalAvecFrais.toLocaleString("fr-MA")} MAD</span>
                </div>
                {subOrders.length > 1 && (
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, textAlign: "center" }}>
                    ℹ️ Cette commande sera divisée en {subOrders.length} sous-commandes (1 par magasin fournisseur)
                  </div>
                )}
              </div>
              <button className="pm-btn-primary" onClick={handleOpenModal}>
                Passer la commande
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══ CHECKOUT MODAL ══ */}
      {showModal && (
        <div className="pm-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="pm-modal" style={{ maxWidth: 680, maxHeight: "90vh", overflowY: "auto" }}>
            <div className="pm-modal__header">
              <div>
                <div className="pm-modal__accent" />
                <h2 className="pm-modal__title">Informations de livraison</h2>
                <p className="pm-modal__sub">Remplissez vos coordonnées et choisissez le mode de paiement</p>
              </div>
              <button className="pm-modal__close" onClick={() => setShowModal(false)}>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            </div>

            <div className="pm-modal__body">
              {/* Nom */}
              <div className="pm-field">
                <label className="pm-field__label">Nom complet</label>
                <div className="pm-field__wrap">
                  <svg className="pm-field__icon" viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                  </svg>
                  <input
                    className={`pm-field__input${formErrors.nom ? " pm-field__input--error" : ""}`}
                    type="text"
                    placeholder="ex : Ahmed Benali"
                    value={orderForm.nom}
                    onChange={(e) => { setOrderForm(p => ({ ...p, nom: e.target.value })); setFormErrors(p => ({ ...p, nom: undefined })); }}
                  />
                </div>
                {formErrors.nom && <span className="pm-field__error">{formErrors.nom}</span>}
              </div>

              {/* Adresse */}
              <div className="pm-field">
                <label className="pm-field__label">Adresse</label>
                <div className="pm-field__wrap">
                  <svg className="pm-field__icon" viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M8 1a4.5 4.5 0 0 0-4.5 4.5c0 3.09 4.5 8.5 4.5 8.5s4.5-5.41 4.5-8.5A4.5 4.5 0 0 0 8 1zm0 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
                  </svg>
                  <input
                    className={`pm-field__input${formErrors.adresse ? " pm-field__input--error" : ""}`}
                    type="text"
                    placeholder="ex : 12 Rue Mohammed V, Casablanca"
                    value={orderForm.adresse}
                    onChange={(e) => { setOrderForm(p => ({ ...p, adresse: e.target.value })); setFormErrors(p => ({ ...p, adresse: undefined })); }}
                  />
                </div>
                {formErrors.adresse && <span className="pm-field__error">{formErrors.adresse}</span>}
              </div>

              {/* Mode livraison */}
              <div className="pm-field">
                <label className="pm-field__label">Mode de livraison</label>
                <div className="pm-livraison-grid">
                  <button
                    type="button"
                    className={`pm-livraison-opt${orderForm.modeLivraison === "magasin" ? " pm-livraison-opt--active" : ""}`}
                    onClick={() => setOrderForm(p => ({ ...p, modeLivraison: "magasin", selectedMagasinId: null }))}
                  >
                    <div className="pm-livraison-opt__icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                    </div>
                    <span className="pm-livraison-opt__label">Magasin</span>
                    <span className="pm-livraison-opt__sub">Retrait en boutique</span>
                    {orderForm.modeLivraison === "magasin" && <span className="pm-livraison-opt__check">✓</span>}
                  </button>
                  <button
                    type="button"
                    className={`pm-livraison-opt${orderForm.modeLivraison === "societe" ? " pm-livraison-opt--active" : ""}`}
                    onClick={() => setOrderForm(p => ({ ...p, modeLivraison: "societe", selectedMagasinId: null }))}
                  >
                    <div className="pm-livraison-opt__icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
                        <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 8" />
                        <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                      </svg>
                    </div>
                    <span className="pm-livraison-opt__label">Société</span>
                    <span className="pm-livraison-opt__sub">Livraison à domicile</span>
                    {orderForm.modeLivraison === "societe" && <span className="pm-livraison-opt__check">✓</span>}
                  </button>
                </div>
              </div>

              {/* Magasin picker */}
              {orderForm.modeLivraison === "magasin" && (
                <div className="pm-field">
                  <label className="pm-field__label">Choisir le magasin de retrait</label>
                  {loadingMagasins ? (
                    <div className="pm-magasin-loading"><span className="pm-spinner" /> Chargement…</div>
                  ) : (
                    <div className="pm-magasin-list">
                      {AllMagasins.map(mag => (
                        <button
                          key={mag.IdMagasin}
                          type="button"
                          className={`pm-magasin-card${orderForm.selectedMagasinId === mag.IdMagasin ? " pm-magasin-card--active" : ""}`}
                          onClick={() => { setOrderForm(p => ({ ...p, selectedMagasinId: mag.IdMagasin })); setFormErrors(p => ({ ...p, selectedMagasinId: undefined })); }}
                        >
                          <div className="pm-magasin-card__icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                          </div>
                          <div className="pm-magasin-card__info">
                            <span className="pm-magasin-card__name">{mag.NomMagasin}</span>
                            <span className="pm-magasin-card__adresse">{mag.adresse_de_siege}</span>
                          </div>
                          {orderForm.selectedMagasinId === mag.IdMagasin && <span className="pm-magasin-card__check">✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {formErrors.selectedMagasinId && <span className="pm-field__error">{formErrors.selectedMagasinId}</span>}
                </div>
              )}

              {/* Société picker */}
              {orderForm.modeLivraison === "societe" && (
                <div className="pm-field">
                  <label className="pm-field__label">Choisir la société de livraison</label>
                  {loadingSocietes ? (
                    <div className="pm-magasin-loading"><span className="pm-spinner" /> Chargement…</div>
                  ) : (
                    <div className="pm-magasin-list">
                      {societes.map(soc => (
                        <button
                          key={soc.id}
                          type="button"
                          className={`pm-magasin-card${selectedSociete?.id === soc.id ? " pm-magasin-card--active" : ""}`}
                          onClick={() => { setSelectedSociete(soc); setFormErrors(p => ({ ...p, selectedSocieteId: undefined })); }}
                        >
                          <div className="pm-magasin-card__icon" style={{ color: "#16a34a" }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                              <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 8" />
                              <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                            </svg>
                          </div>
                          <div className="pm-magasin-card__info">
                            <span className="pm-magasin-card__name">{soc.NomSociete}</span>
                            <span className="pm-magasin-card__adresse">{soc.AdresseSociete}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#16a34a", marginTop: 2 }}>
                              {parseFloat(soc.Prix).toFixed(2)} MAD
                            </span>
                          </div>
                          {selectedSociete?.id === soc.id && <span className="pm-magasin-card__check">✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {formErrors.selectedSocieteId && <span className="pm-field__error">{formErrors.selectedSocieteId}</span>}
                </div>
              )}

              {/* ── Per-Seller Payment Method ── */}
              <div className="pm-field" style={{ marginTop: 24 }}>
                <label className="pm-field__label" style={{ fontSize: 13, fontWeight: 800, marginBottom: 14, letterSpacing: "0.4px" }}>
                  💳 Mode de paiement par fournisseur
                </label>

                {loadingEnCompte ? (
                  <div className="pm-magasin-loading">
                    <span className="pm-spinner" />
                    <span>Vérification du compte fournisseur en cours…</span>
                  </div>
                ) : (
                  subOrders.map((sub, idx) => {
                    const ec = sub.enCompte;
                    const canUseSolde = ec?.en_compte && ec.solde >= sub.total;
                    const soldeLow    = ec?.en_compte && ec.solde > 0 && ec.solde < sub.total;
                    const isSolde     = sub.paymentMethod === "solde";
                    const isRib       = sub.paymentMethod === "rib";

                    return (
                      <div
                        key={sub.sellerName}
                        className={`pm-supplier-card ${isSolde ? "pm-supplier-card--solde" : isRib ? "pm-supplier-card--rib" : ""}`}
                      >
                        {/* ── Card Header ── */}
                        <div className="pm-supplier-card__header">
                          <div className="pm-supplier-card__seller">
                            <div className="pm-supplier-card__icon">🏪</div>
                            <div>
                              <div className="pm-supplier-card__name">{sub.sellerName}</div>
                              <div className="pm-supplier-card__tag">Fournisseur</div>
                            </div>
                          </div>
                          <div className="pm-supplier-card__total">
                            {sub.total.toLocaleString("fr-MA")} MAD
                          </div>
                        </div>

                        {/* ── En-Compte Status ── */}
                        <div className="pm-supplier-card__body">
                          {ec?.en_compte ? (
                            <div className={`pm-solde-status ${canUseSolde ? "pm-solde-status--ok" : "pm-solde-status--low"}`}>
                              <span style={{ fontSize: 16 }}>{canUseSolde ? "✅" : "⚠️"}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700 }}>
                                  En compte — Solde : {Number(ec.solde).toLocaleString("fr-MA")} MAD
                                </div>
                                {soldeLow && (
                                  <div style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    background: "rgba(239,68,68,0.12)",
                                    border: "1.5px solid rgba(239,68,68,0.45)",
                                    borderRadius: 8,
                                    color: "#ef4444",
                                    fontWeight: 600,
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}>
                                    <span style={{ fontSize: 15 }}>🚫</span>
                                    <span>
                                      Votre solde{" "}
                                      <strong>({Number(ec!.solde).toLocaleString("fr-MA")} MAD)</strong>{" "}
                                      est insuffisant pour cette commande{" "}
                                      <strong>({sub.total.toLocaleString("fr-MA")} MAD requis)</strong>.
                                    </span>
                                  </div>
                                )}
                                {canUseSolde && (
                                  <div className="pm-solde-progress-bar">
                                    <div
                                      className="pm-solde-progress-fill"
                                      style={{ width: `${Math.min(100, (sub.total / ec.solde) * 100)}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="pm-solde-status pm-solde-status--none">
                              <span style={{ fontSize: 15 }}>ℹ️</span>
                              <span>Aucun compte ouvert avec ce fournisseur</span>
                            </div>
                          )}

                          {/* ── Premium Horizontal Payment Method Selector ── */}
                          <div className="pm-pay-methods">

                            {/* ── Solde option ── */}
                            <button
                              type="button"
                              disabled={!canUseSolde}
                              onClick={() => setSubOrders(prev => prev.map((o, i) => i === idx ? { ...o, paymentMethod: "solde" } : o))}
                              className={`pm-pay-method${isSolde ? " pm-pay-method--active pm-pay-method--solde-active" : ""}${!canUseSolde ? " pm-pay-method--disabled" : ""}`}
                            >
                              <div className={`pm-pay-method__radio${isSolde ? " pm-pay-method__radio--solde" : ""}`}>
                                {isSolde && <div className="pm-pay-method__radio-dot" />}
                              </div>
                              <div className={`pm-pay-method__icon-wrap${isSolde ? " pm-pay-method__icon-wrap--solde" : ""}`}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="18" height="18">
                                  <rect x="1" y="4" width="22" height="16" rx="2"/>
                                  <line x1="1" y1="10" x2="23" y2="10"/>
                                </svg>
                              </div>
                              <div className="pm-pay-method__info">
                                <span className="pm-pay-method__title">Payer par solde</span>
                                <span className="pm-pay-method__sub">
                                  {canUseSolde && ec
                                    ? `${Number(ec.solde).toLocaleString("fr-MA")} MAD disponible`
                                    : !ec?.en_compte ? "Aucun compte ouvert" : "Solde insuffisant"}
                                </span>
                              </div>
                              {canUseSolde && <span className="pm-pay-method__pill pm-pay-method__pill--green">Disponible</span>}
                            </button>

                            {/* ── RIB / Livraison option ── */}
                            <button
                              type="button"
                              onClick={() => setSubOrders(prev => prev.map((o, i) => i === idx ? { ...o, paymentMethod: "rib" } : o))}
                              className={`pm-pay-method${isRib ? " pm-pay-method--active pm-pay-method--rib-active" : ""}`}
                            >
                              <div className={`pm-pay-method__radio${isRib ? " pm-pay-method__radio--rib" : ""}`}>
                                {isRib && <div className="pm-pay-method__radio-dot pm-pay-method__radio-dot--rib" />}
                              </div>
                              <div className={`pm-pay-method__icon-wrap${isRib ? " pm-pay-method__icon-wrap--rib" : ""}`}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="18" height="18">
                                  <rect x="1" y="3" width="15" height="13"/>
                                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                                  <circle cx="5.5" cy="18.5" r="2.5"/>
                                  <circle cx="18.5" cy="18.5" r="2.5"/>
                                </svg>
                              </div>
                              <div className="pm-pay-method__info">
                                <span className="pm-pay-method__title">À la livraison / RIB</span>
                                <span className="pm-pay-method__sub">Virement ou règlement à réception</span>
                              </div>
                              <span className="pm-pay-method__pill pm-pay-method__pill--amber">Standard</span>
                            </button>

                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="pm-modal__footer">
              {orderForm.modeLivraison === "magasin" && selectedMagasin && (
                <div className="pm-modal__recap">
                  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M8 1a4.5 4.5 0 0 0-4.5 4.5c0 3.09 4.5 8.5 4.5 8.5s4.5-5.41 4.5-8.5A4.5 4.5 0 0 0 8 1zm0 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
                  </svg>
                  Retrait : <strong>{selectedMagasin.NomMagasin}</strong> — {selectedMagasin.adresse_de_siege}
                </div>
              )}
              {orderForm.modeLivraison === "societe" && selectedSociete && (
                <div className="pm-modal__recap">
                  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M8 1a4.5 4.5 0 0 0-4.5 4.5c0 3.09 4.5 8.5 4.5 8.5s4.5-5.41 4.5-8.5A4.5 4.5 0 0 0 8 1zm0 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
                  </svg>
                  Livraison : <strong>{selectedSociete.NomSociete}</strong> — {currentFrais.toFixed(2)} MAD
                </div>
              )}

              {/* Per-order payment summary */}
              <div className="pm-modal-recap-box">
                {subOrders.map(sub => (
                  <div key={sub.sellerName} className="pm-modal-recap-item">
                    <span>
                      {sub.sellerName} &middot; {sub.paymentMethod === "solde" ? "💳 Solde" : "🏦 À la livraison/RIB"}
                    </span>
                    <strong>{sub.total.toLocaleString("fr-MA")} MAD</strong>
                  </div>
                ))}
              </div>

              <div className="pm-modal__total-preview">
                <span>Total à payer</span>
                <span className="pm-modal__total-amt">{totalAvecFrais.toLocaleString("fr-MA")} MAD</span>
              </div>
              <button
                className="pm-btn-primary pm-btn-primary--full"
                onClick={handleConfirm}
                disabled={confirmingOrder || loadingEnCompte}
              >
                {confirmingOrder ? "Envoi en cours..." : "Confirmer la commande"}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Payment Modal */}
      {showPayment && (
        <QrPaymentModal
          commandeId={commandeIdForPayment}
          onBack={() => setShowPayment(false)}
          onClose={() => { setShowPayment(false); window.location.href = `/magasins/${MagasinId}`; }}
        />
      )}

      {/* Overlay Loader */}
      {confirmingOrder && <OverlayLoader message={orderProgressMessage} />}
    </>
  );
}